/* @flow */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ReactAccessConsumer } from 'react-access';

import NavigationService from 'services/NavigationService';
import * as reportingPermissions from 'permissions/reporting';
import * as generalPermissions from 'permissions/general';
import TableButton from 'components/TableButton';
import { formulateUri } from 'tables/components/ReportView/state';
import RegionStore from 'stores/RegionStore';

import createReportView from '../components/ReportView/ReportViewBase';
import type { ViewConfigGetter } from '../components/ReportView/ReportViewBase';
import type { DataViewColumn } from '../components/ReportView/types';
import type { ReportViewState } from '../components/ReportView/state';

const SUMMARY_DATA_PATHNAME = 'overall-stats';

const columns = (state: ReportViewState, props: Object): DataViewColumn[] => {
  const regionFilter = (state.filters || []).find(
    item => item.key === RegionStore.REGION_FILTER_KEY
  );
  const regionScope =
    regionFilter &&
    regionFilter.values &&
    regionFilter.values.some(value => value && String(value).toUpperCase() !== 'ALL')
      ? [regionFilter]
      : [];
  const isUnassignedBrand = (rowData: Object) => !rowData.brand_id;
  const assignedBrandFilter = (rowData: Object) => [
    { key: 'brand_id', values: [rowData.brand_id] },
  ];
  const unassignedBrandFilter = [{ key: 'unbranded', values: ['true'] }];
  const orderAssetFilters = (rowData: Object, assignedExtras, unassignedExtras) =>
    isUnassignedBrand(rowData)
      ? [...unassignedBrandFilter, ...regionScope, ...unassignedExtras]
      : [...assignedBrandFilter(rowData), ...assignedExtras];

  return [
    {
      columnName: 'brand_name',
      title: 'general:reporting.brands.labels.name',
      flexBasis: 300,
      component: function BrandNameCell({ display, rowData, t }) {
        if (isUnassignedBrand(rowData)) {
          const regionId = (regionFilter && regionFilter.values && regionFilter.values[0]) || 'ALL';
          const region =
            props.regionStore && props.regionStore.regions && props.regionStore.regions[regionId];
          const regionLabel =
            String(regionId).toUpperCase() === 'ALL'
              ? 'All region'
              : t(`general:regions.${(region && region.name) || regionId}`);

          return <span>{`Unassigned brand - ${regionLabel}`}</span>;
        }

        return (
          <Link
            to={formulateUri(`reporting/brand/${rowData.brand_id}`, {
              page: 1,
              ...(regionFilter ? { filters: [regionFilter] } : {}),
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'trading_accounts_count',
      title: 'general:reporting.brands.labels.accounts',
      noFlexGrow: true,
      flexBasis: 110,
      component: function TradingAccountsCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('accounts', {
              page: 1,
              filters: rowData.brand_id
                ? [{ key: 'brand_id', values: [rowData.brand_id] }]
                : [{ key: 'unbranded', values: ['true'] }],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_contracts_open_count',
      title: 'general:reporting.brands.labels.open_contracts',
      noFlexGrow: true,
      flexBasis: 110,
      component: function ContractsOpenCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('orders', {
              page: 1,
              filters: [
                ...orderAssetFilters(
                  rowData,
                  [{ key: 'er_status', values: ['open'] }],
                  [{ key: 'er_status', values: ['open'], overrideImplicit: true }]
                ),
              ],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_contracts_closed_count',
      title: 'general:reporting.brands.labels.closed_contracts',
      noFlexGrow: true,
      flexBasis: 110,
      component: function ContractsClosedCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('orders', {
              page: 1,
              filters: [
                ...orderAssetFilters(
                  rowData,
                  [{ key: 'er_status', values: ['closed'], overrideImplicit: true }],
                  [{ key: 'er_status', values: ['closed'], overrideImplicit: true }]
                ),
              ],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_assets_total_rental_count',
      title: 'general:reporting.brands.labels.rental_assets',
      noFlexGrow: true,
      flexBasis: 110,
      component: function AssetsRentalCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('assets', {
              page: 1,
              filters: [
                ...orderAssetFilters(
                  rowData,
                  [{ key: 'asset_type', values: ['Rented Open'] }],
                  [
                    {
                      key: 'reporting_stats_type',
                      values: ['rental'],
                      overrideImplicit: true,
                    },
                  ]
                ),
              ],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_assets_on_sold_count',
      title: 'general:reporting.brands.labels.sale_assets',
      noFlexGrow: true,
      flexBasis: 110,
      component: function AssetsSaleCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('assets', {
              page: 1,
              filters: [
                ...orderAssetFilters(
                  rowData,
                  [{ key: 'asset_type', values: ['Purchased'] }],
                  [
                    {
                      key: 'reporting_stats_type',
                      values: ['sale'],
                      overrideImplicit: true,
                    },
                  ]
                ),
              ],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_assets_owned_count',
      title: 'general:reporting.brands.labels.owned_assets',
      noFlexGrow: true,
      flexBasis: 110,
      component: function AssetsOwnedCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('assets', {
              page: 1,
              filters: [
                ...orderAssetFilters(
                  rowData,
                  [{ key: 'asset_type', values: ['Owned'] }],
                  [
                    {
                      key: 'reporting_stats_type',
                      values: ['owned'],
                      overrideImplicit: true,
                    },
                  ]
                ),
              ],
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'brand_unique_users_count',
      title: 'general:reporting.brands.labels.users',
      noFlexGrow: true,
      flexBasis: 110,
      component: function UsersCountCell({ display, rowData }) {
        return (
          <ReactAccessConsumer>
            {isAuthorised => {
              if (isAuthorised(generalPermissions.userManagement)) {
                return (
                  <Link
                    to={formulateUri('users', {
                      page: 1,
                      filters: isUnassignedBrand(rowData)
                        ? [
                            ...unassignedBrandFilter,
                            { key: 'is_enabled', values: ['Enabled'] },
                            { key: 'customer_users', values: ['true'] },
                          ]
                        : assignedBrandFilter(rowData),
                    })}
                  >
                    <span>{display}</span>
                  </Link>
                );
              }

              return display;
            }}
          </ReactAccessConsumer>
        );
      },
    },
    {
      columnName: 'er_account_projects_count',
      title: 'general:reporting.brands.labels.projects',
      noFlexGrow: true,
      flexBasis: 110,
      component: function ProjectsCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('/projects', {
              page: 1,
              filters: isUnassignedBrand(rowData)
                ? [...unassignedBrandFilter, ...regionScope]
                : assignedBrandFilter(rowData),
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_account_cost_centres_count',
      title: 'general:reporting.brands.labels.cost_centres',
      noFlexGrow: true,
      flexBasis: 110,
      component: function CostCentresCountCell({ display, rowData }) {
        return (
          <Link
            to={formulateUri('/cost-centers', {
              page: 1,
              filters: isUnassignedBrand(rowData)
                ? [...unassignedBrandFilter, ...regionScope]
                : assignedBrandFilter(rowData),
            })}
          >
            <span>{display}</span>
          </Link>
        );
      },
    },
    {
      columnName: 'er_account_users_unique_login_count',
      title: 'general:reporting.brands.labels.unique_user_logins',
      noFlexGrow: true,
      flexBasis: 110,
    },
    {
      columnName: 'brand_id',
      title: '',
      flexBasis: 80,
      noFlexGrow: true,
      disableSort: true,
      permissions: reportingPermissions.updateBrands,
      component: function EditButtonCell({ rowData }) {

        if(!rowData.brand_id) {
          return null;
        }
        
        const handleEditButtonClick = () => {
          setImmediate(() => {
            NavigationService.navigateTo(`brands/edit/${rowData.brand_id}`);
          });
        };

        return <TableButton label="Edit" iconHref="#icon-edit" onClick={handleEditButtonClick} />;
      },
    },
  ];
};

const viewConfig: ViewConfigGetter = (props, state, getters) => ({
  title: 'general:reporting.brands.title',
  codename: 'reporting-brands',
  type: 'SINGLE_WITH_SUMMARY',
  userFilterNamespace: 'Brands', // REVIEW: This is not applicable - look into making it optional
  disableFilters: true,
  disableFilterChips: true,
  disableUserPreferenceSave: true,
  downloadPermissions: reportingPermissions.downloadBrands,
  enableRegionSelect: false,
  view: {
    dataPathname: `brand-stats`,
    apiType: 'SQL',
    columns: columns(state, props),
  },
  defaultFilters: props.userStore.userLoaded
    ? [
        {
          key: RegionStore.REGION_FILTER_KEY,
          values: [props.regionStore.regionOptionsInfo.suggestedDefault],
          fixed: true,
        },
      ]
    : undefined,
  summaryView: {
    title: { text: 'general:reporting.brands.summary.title' },
    sectionHeading: 'general:reporting.brands.summary.section_heading',
    dataPathname: SUMMARY_DATA_PATHNAME,
    enableRegionSelect: true,
    sectionHeadingButtons: [
      {
        label: 'general:actions.export_summary',
        icon: '#icon-download-page',
        onClick: event => {
          if (event && event.preventDefault) {
            event.preventDefault();
          }

          if (event && event.stopPropagation) {
            event.stopPropagation();
          }

          const { region } = getters.getSummaryViewInfo();
          const fallbackRegion = props.regionStore.regionOptionsInfo.suggestedDefault;
          const exportRegion = region || fallbackRegion || 'ALL';

          return props.reportViewStore.exportSummary(SUMMARY_DATA_PATHNAME, {
            region: exportRegion,
          });
        },
        permissions: reportingPermissions.viewAllBrands,
      },
    ],
    mapDataToLayout: data => {
      let targetItem = {};
      const allItem = data.find(item => item.region_id === props.regionStore.allRegionId);

      if (allItem) {
        targetItem = allItem;
        targetItem.regionalData = data.reduce((_regionalData, item) => {
          if (item.region_id !== props.regionStore.allRegionId) {
            _regionalData[item.region_id] = item;
          }

          return _regionalData;
        }, {});
      } else {
        [targetItem = {}] = data;
      }

      const {
        trading_accounts_count,
        account_count_atleast_oneuser_login,
        brand_count,
        brand_count_atleast_oneuser,
        brand_count_atleast_oneuser_login,
        er_contracts_open_count,
        er_contracts_closed_count,
        er_assets_total_rental_count,
        er_assets_on_sold_count,
        er_assets_owned_count,
        er_account_users_count,
        er_account_projects_count,
        er_account_projects_current_year_count,
        er_account_projects_current_month_count,
        er_account_cost_centres_count,
        er_account_cost_centres_current_year_count,
        er_account_cost_centres_current_month_count,
        er_account_users_unique_login_count,
        er_account_users_login_count,
        er_account_users_current_month_unique_login_count,
        er_account_users_current_year_unique_login_count,
        er_account_users_current_month_login_count,
        er_account_users_current_year_login_count,

        request_contract_extension_count,
        request_contract_extension_current_month_count,
        request_contract_extension_current_year_count,

        request_asset_rental_extension_count,
        request_asset_rental_extension_current_month_count,
        request_asset_rental_extension_current_year_count,

        quote_request_count,
        quote_request_current_month_count,
        quote_request_current_year_count,

        new_asset_request_count,
        new_asset_request_current_month_count,
        new_asset_request_current_year_count,

        asset_calibration_request_count,
        asset_calibration_request_current_month_count,
        asset_calibration_request_current_year_count,

        new_license_request_count,
        new_license_request_current_month_count,
        new_license_request_current_year_count,

        asset_fault_count,
        asset_fault_current_month_count,
        asset_fault_current_year_count,

        unique_users_count,
        unique_users_count_mtd,
        unique_users_count_ytd,

        disabled_unique_users_count,
        disabled_unique_users_count_ytd,
        disabled_unique_users_count_mtd,
      } = targetItem;

      const brandsWithZeroUsers = brand_count - brand_count_atleast_oneuser_login;
      const accountsWithZeroUsers = trading_accounts_count - account_count_atleast_oneuser_login;

      const layout = [
        [
          {
            title: 'general:reporting.brands.summary.main_card_title',
            columns: [{ heading: 'general:reporting.summary_titles.summary' }],
            data: [
              {
                label: 'general:reporting.summary_labels.brand_families',
                data: [brand_count],
              },
              {
                label: 'general:reporting.summary_labels.brands_with_registered_users',
                data: [brand_count_atleast_oneuser],
              },
              {
                label: 'general:reporting.summary_labels.brand_families_with_users',
                data: [brand_count_atleast_oneuser_login],
              },
              {
                label: 'general:reporting.summary_labels.trading_accounts',
                data: [trading_accounts_count],
              },
              {
                label: 'general:reporting.summary_labels.open_contracts',
                data: [er_contracts_open_count],
              },
              {
                label: 'general:reporting.summary_labels.closed_contracts',
                data: [er_contracts_closed_count],
              },
              {
                label: 'general:reporting.summary_labels.rental_assets',
                data: [er_assets_total_rental_count],
              },
              {
                label: 'general:reporting.summary_labels.sale_assets',
                data: [er_assets_on_sold_count],
              },
              {
                label: 'general:reporting.summary_labels.owned_assets',
                data: [er_assets_owned_count],
              },
            ],
          },
        ],
        [
          ...(allItem && !props.regionStore.regionsLoading
            ? [
                {
                  title: 'general:reporting.summary_titles.trading_accounts',
                  columns: Object.keys(targetItem.regionalData).map(regionId => ({
                    heading:
                      props.regionStore.regions[regionId] &&
                      props.regionStore.regions[regionId].name
                        ? `general:regions.${props.regionStore.regions[regionId].name}`
                        : 'Unknown',
                  })),
                  data: [
                    {
                      label: 'general:reporting.summary_labels.by_region',
                      data: Object.keys(targetItem.regionalData).map(regionId =>
                        targetItem.regionalData[regionId]
                          ? targetItem.regionalData[regionId].trading_accounts_count
                          : 'N/A'
                      ),
                    },
                    {
                      label: 'general:reporting.summary_labels.with_users_logged_in',
                      data: Object.keys(targetItem.regionalData).map(regionId =>
                        targetItem.regionalData[regionId]
                          ? targetItem.regionalData[regionId].account_count_atleast_oneuser_login
                          : '0'
                      ),
                    },
                  ],
                },
              ]
            : []),
          {
            title: 'general:reporting.summary_titles.usage',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.registered_active_users',
                data: [unique_users_count, unique_users_count_ytd, unique_users_count_mtd],
              },
              {
                label: 'general:reporting.summary_labels.registered_inactive_users',
                data: [
                  disabled_unique_users_count,
                  disabled_unique_users_count_ytd,
                  disabled_unique_users_count_mtd,
                ],
              },
              {
                label: 'general:reporting.summary_labels.users_login',
                data: [
                  er_account_users_login_count,
                  er_account_users_current_year_login_count,
                  er_account_users_current_month_login_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.users_unique_login',
                data: [
                  er_account_users_unique_login_count,
                  er_account_users_current_year_unique_login_count,
                  er_account_users_current_month_unique_login_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.process_requests',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.asset_request_submissions',
                data: [
                  new_asset_request_count,
                  new_asset_request_current_year_count,
                  new_asset_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.license_request_submissions',
                data: [
                  new_license_request_count,
                  new_license_request_current_year_count,
                  new_license_request_current_month_count,
                ],
              },
            ],
          },
        ],
        [
          {
            title: 'general:reporting.summary_titles.customer_created_content',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.projects',
                data: [
                  er_account_projects_count,
                  er_account_projects_current_year_count,
                  er_account_projects_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.cost_centres',
                data: [
                  er_account_cost_centres_count,
                  er_account_cost_centres_current_year_count,
                  er_account_cost_centres_current_month_count,
                ],
              },
            ],
          },
          {
            title: 'general:reporting.summary_titles.asset_activity',
            columns: [
              { heading: 'general:reporting.summary_titles.total' },
              { heading: 'general:reporting.summary_titles.ytd' },
              { heading: 'general:reporting.summary_titles.mtd' },
            ],
            data: [
              {
                label: 'general:reporting.summary_labels.contract_extensions',
                data: [
                  request_contract_extension_count,
                  request_contract_extension_current_year_count,
                  request_contract_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.asset_extensions',
                data: [
                  request_asset_rental_extension_count,
                  request_asset_rental_extension_current_year_count,
                  request_asset_rental_extension_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.quote_requests',
                data: [
                  quote_request_count,
                  quote_request_current_year_count,
                  quote_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.calibration_requests',
                data: [
                  asset_calibration_request_count,
                  asset_calibration_request_current_year_count,
                  asset_calibration_request_current_month_count,
                ],
              },
              {
                label: 'general:reporting.summary_labels.faults_reported',
                data: [
                  asset_fault_count,
                  asset_fault_current_year_count,
                  asset_fault_current_month_count,
                ],
              },
            ],
          },
        ],
      ];

      return layout;
    },
  },
  buttons: [
    {
      label: 'general:actions.new',
      icon: '#icon-add',
      to: '/brands/create',
      permissions: reportingPermissions.createBrands,
    },
  ],
});

export default createReportView(viewConfig);
